import { Injectable } from '@angular/core'
import { AuthData, AuthUserData, ProfileSettings, Token } from '@app/v2/auth/types/auth-data.type'
import { User as V2User } from '@app/v2/shared/types/user.type'

/**
 * V3 User interface based on the original V3 structure
 */
export interface V3User {
  id: number
  email: string
  old_email: string | null
  token: string
  is_admin: boolean
  last_login: string
  confirmed: boolean
  profile_picture: string
  full_name: string
  phone_number: string
  job_title: string | null
  options: {
    display_tutorial?: boolean
    email_status: {
      valid: boolean
      reason: string
      last_update: string
    }
  }
}

/**
 * Transformed V3 User interface - extends V3User with additional V2 properties
 */
export interface TransformedV3User extends V3User {
  // Additional V2 properties that don't have V3 equivalents
  mfa_enabled?: boolean
  mfa_channel?: string | null
  suggest_mfa?: boolean
  has_password?: boolean
  has_passkeys?: boolean
  social_login_only?: boolean
  pictures?: any[]
  seen_at?: string
  // External token object from V4
  v2_token?: Token | null
  profile_settings?: ProfileSettings
}

/**
 * Transformer interface for converting between different user formats
 */
export interface UserTransformer<T, U> {
  transformV4ToV3(input: AuthData): TransformedV3User
}

/**
 * Service that handles transformation between V2, V4 and V3 user objects
 * Implements the Transformer pattern for clean, maintainable code
 */
@Injectable({
  providedIn: 'root'
})
export class UserTransformerService implements UserTransformer<V2User, TransformedV3User> {
  // Type guard to check if input is a raw V3User object
  isV3User(data: AuthData | V3User): data is V3User {
    return 'email' in data && !('user' in data)
  }

  // Type guard to check if a value is a Token
  isToken(token: unknown): token is Token {
    return typeof token === 'object' && token !== null && 'access_token' in token
  }

  // Type guard to check if object is AuthUserData
  isAuthUserData(user: unknown): user is AuthUserData {
    return typeof user === 'object' && user !== null && 'email' in user
  }

  /**
   * Transforms V4 user object (AuthData) to V3 user object
   * @param v4AuthData - The V4 AuthData object containing user and token, or a raw user object
   * @returns TransformedV3User object with both string token and external token object
   */
  public transformV4ToV3(v4AuthData: AuthData | V3User): TransformedV3User {
    if (!v4AuthData) {
      throw new Error('V4 AuthData object with user is required for transformation')
    }

    let userData: V3User | AuthUserData | null
    let token: string
    let profileSettings: ProfileSettings | undefined

    if (this.isV3User(v4AuthData)) {
      // Already a V3User
      userData = v4AuthData
      token = v4AuthData.token || ''
      profileSettings = undefined
    } else {
      // AuthData structure
      userData = v4AuthData.user
      token = v4AuthData.token?.access_token || v4AuthData.token?.toString?.() || ''
      // profileSettings = v4AuthData.profile_settings;
    }

    // Base V3 user object
    const v3User: V3User = {
      id: userData?.id,
      email: userData?.email,
      old_email: null, // V4 doesn't have old_email concept
      token: token, // Include token inside the user object as string
      is_admin: userData?.is_admin ?? false, // V4 doesn't have is_admin field, default to false
      last_login: this._formatLastLogin(!this.isV3User(v4AuthData) ? v4AuthData.token?.lastLoggedIn : undefined),
      confirmed: userData?.confirmed ?? false, // V4 doesn't have confirmed field, default to false
      profile_picture: this.isAuthUserData(userData) ? (userData as any).profile_picture || this.extractProfilePicture(userData.avatar) : '',
      full_name: userData?.full_name || '',
      phone_number: this.isAuthUserData(userData) && 'phone_number' in userData ? (userData as { phone_number?: string }).phone_number || '' : '',
      job_title: this.isAuthUserData(userData) && 'job_title' in userData ? (userData as { job_title?: string | null }).job_title || null : null,
      options: {
        email_status: {
          valid: true,
          reason: '',
          last_update: new Date().toISOString().replace('T', ' ').substring(0, 19)
        },
        display_tutorial: profileSettings?.display_tutorial || false
      }
    }

    const result: TransformedV3User = {
      ...v3User,
      mfa_enabled: this.isAuthUserData(userData) ? userData?.mfa_enabled : undefined,
      mfa_channel: this.isAuthUserData(userData) ? ((userData as any)?.mfa_channel ?? null) : undefined,
      suggest_mfa: this.isAuthUserData(userData) ? userData?.suggest_mfa : undefined,
      has_password: this.isAuthUserData(userData) ? Boolean((userData as any)?.has_password) : undefined,
      has_passkeys: this.isAuthUserData(userData) ? Boolean((userData as any)?.has_passkeys) : undefined,
      social_login_only: this.isAuthUserData(userData) ? Boolean((userData as any)?.social_login_only) : undefined,
      v2_token: !this.isV3User(v4AuthData) && this.isToken(v4AuthData.token) ? v4AuthData.token : null,
      profile_settings: profileSettings
    }

    return result
  }

  /**
   * Extracts profile picture URL from V2 avatar object
   * @param avatar - V2 avatar object
   * @returns Profile picture URL string
   */
  private extractProfilePicture(avatar: V2User['avatar']): string {
    if (!avatar) {
      return ''
    }
    return avatar.cropped_src || avatar.original_src || ''
  }

  /**
   * Formats last login timestamp from V4 token data
   * @param lastLoggedIn - The lastLoggedIn timestamp from V4 token
   * @returns Formatted last login string
   */
  private _formatLastLogin(lastLoggedIn?: number): string {
    if (!lastLoggedIn) {
      return new Date().toISOString().replace('T', ' ').substring(0, 19)
    }

    // Convert timestamp to Date and format
    const date = new Date(lastLoggedIn)
    return date.toISOString().replace('T', ' ').substring(0, 19)
  }
}
